import React from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, Building2, ScrollText, Flag, LogOut, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useOwnerAuth } from "@/lib/owner-auth-context";

const menu = [
  { to: "/owner", label: "Platform Overview", icon: LayoutDashboard, exact: true },
  { to: "/owner/merchants", label: "Merchant Management", icon: Building2 },
  { to: "/owner/audit", label: "Audit Log", icon: ScrollText },
  { to: "/owner/flags", label: "Feature Flags", icon: Flag },
  { to: "/owner/cms", label: "CMS Landing", icon: Flag },
  { to: "/owner/security", label: "Security (2FA)", icon: Flag },
];

const NavLink = ({ item }) => {
  const { pathname } = useLocation();
  const active = item.exact ? pathname === item.to : pathname.startsWith(item.to);
  const Icon = item.icon;
  return (
    <Link to={item.to} data-testid={`nav-owner-${item.to.replace(/\//g, "-").slice(1)}`}
      className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
        active ? "bg-emerald-900/40 text-emerald-300 border border-emerald-800" : "text-gray-400 hover:bg-gray-800 hover:text-white border border-transparent"
      }`}>
      <Icon size={17} />
      {item.label}
    </Link>
  );
};

export default function OwnerLayout() {
  const { owner, logout } = useOwnerAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/owner/login");
  };

  return (
    <div className="min-h-screen bg-gray-950 text-gray-100">
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col bg-gray-900 border-r border-gray-800 z-30">
        <div className="h-16 px-5 flex items-center border-b border-gray-800 gap-2">
          <div className="h-8 w-8 rounded-lg bg-emerald-600 flex items-center justify-center">
            <ShieldCheck className="text-white" size={18} />
          </div>
          <div>
            <div className="font-heading font-bold text-white text-sm">Custora</div>
            <div className="text-[9px] font-semibold uppercase tracking-widest text-emerald-400">Platform Console</div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {menu.map((item) => <NavLink key={item.to} item={item} />)}
        </nav>
        <div className="p-3 border-t border-gray-800">
          <div className="px-2 py-2">
            <div className="text-sm font-semibold text-white truncate">{owner?.name}</div>
            <div className="text-xs text-gray-500 truncate">{owner?.email}</div>
            <Badge className="mt-2 bg-emerald-900/40 text-emerald-300 border border-emerald-800 text-[10px]">Super Owner</Badge>
          </div>
          <Button variant="ghost" size="sm" onClick={handleLogout} className="w-full mt-2 text-gray-400 hover:text-white hover:bg-gray-800 justify-start" data-testid="owner-logout">
            <LogOut className="mr-2 h-4 w-4" /> Keluar
          </Button>
        </div>
      </aside>
      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 h-16 bg-gray-900 border-b border-gray-800 flex items-center px-4 lg:px-8 gap-3">
          <div className="lg:hidden">
            <ShieldCheck className="text-emerald-400" size={22} />
          </div>
          <div className="flex-1">
            <div className="text-xs text-gray-500">Super Owner Console</div>
            <div className="text-sm font-semibold text-white">Custora Platform Admin</div>
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
